/**
 * Seleção & Scorecard (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_scorecard.py).
 *
 * Na URL: CNPJs (vírgula), período, métricas ("chave:peso:janela", vazio = padrão do
 * legado, "-" = nenhuma), fundos do gráfico ("|") e CDI. Os CNPJs são texto livre: só vão
 * para a URL ao clicar em Calcular. O resultado não some ao mexer no gráfico.
 */
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Menu,
  MultiSelect,
  NumberInput,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, CampoData, GrupoFiltros } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';
const CAMINHO = '/fundos/scorecard';
export const METRICAS = [
  {
    chave: 'retorno',
    grupo: 'retorno',
    nome: 'Retorno',
    janela: 12,
    descricao: 'Retorno acumulado no período (↑ maior melhor)',
  },
  {
    chave: 'consistencia',
    grupo: 'consistencia',
    nome: 'Consistência',
    janela: 24,
    descricao: '% de meses acima do CDI (↑)',
  },
  {
    chave: 'volatilidade',
    grupo: 'risco',
    nome: 'Volatilidade',
    janela: 12,
    descricao: 'Volatilidade anualizada dos retornos diários (↓ menor melhor)',
  },
  {
    chave: 'max_dd',
    grupo: 'risco',
    nome: 'Max Drawdown',
    janela: 24,
    descricao: 'Maior queda do pico ao vale (↓)',
  },
  {
    chave: 'sharpe',
    grupo: 'risco_retorno',
    nome: 'Sharpe',
    janela: 12,
    descricao: 'Retorno excedente ao CDI / volatilidade (↑)',
  },
  {
    chave: 'sortino',
    grupo: 'risco_retorno',
    nome: 'Sortino',
    janela: 12,
    descricao: 'Retorno excedente ao CDI / desvio negativo (↑)',
  },
  {
    chave: 'calmar',
    grupo: 'risco_retorno',
    nome: 'Calmar',
    janela: 24,
    descricao: 'Retorno anualizado / |Max Drawdown| (↑)',
  },
];
const GRUPOS: Record<string, string> = {
  retorno: 'Retorno',
  consistencia: 'Consistência',
  risco: 'Risco',
  risco_retorno: 'Risco-retorno',
};
const ESPERA_MS = 500; // peso e janela vão para a URL (e para a API) quando a digitação para
interface Param {
  peso: number;
  janela: number;
}
/** "retorno:1:12,..." → mapa; "" = padrão (todas, peso 1); "-" = nenhuma. */
export function lerMetricas(texto: string): Record<string, Param> {
  if (texto === '')
    return Object.fromEntries(METRICAS.map((m) => [m.chave, { peso: 1, janela: m.janela }]));
  const saida: Record<string, Param> = {};
  if (texto === '-') return saida;
  for (const item of texto.split(',')) {
    const [chave = '', peso, janela] = item.split(':');
    if (METRICAS.some((m) => m.chave === chave))
      saida[chave] = { peso: Number(peso), janela: Number(janela) };
  }
  return saida;
}
/**
 * Fração do score que cada métrica representa, como o compute_scores do backend: dentro
 * do grupo, peso / soma dos pesos; cada grupo pesa a média dos pesos das suas métricas.
 * Métrica desligada ou com peso 0 fica de fora.
 */
export function pesosEfetivos(params: Record<string, Param>): Record<string, number> {
  const grupos = new Map<string, [string, number][]>();
  for (const m of METRICAS) {
    const p = params[m.chave];
    if (p && p.peso > 0) grupos.set(m.grupo, [...(grupos.get(m.grupo) ?? []), [m.chave, p.peso]]);
  }
  const soma = (itens: [string, number][]) => itens.reduce((t, [, w]) => t + w, 0);
  const total = [...grupos.values()].reduce((t, itens) => t + soma(itens) / itens.length, 0);
  const saida: Record<string, number> = {};
  for (const itens of grupos.values()) {
    const pesoGrupo = soma(itens) / itens.length / total;
    for (const [chave, w] of itens) saida[chave] = (w / soma(itens)) * pesoGrupo;
  }
  return saida;
}
function formatarPeso(fracao: number | undefined) {
  return fracao === undefined
    ? '—'
    : `${(fracao * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}
export function escreverMetricas(params: Record<string, Param>): string {
  const texto = METRICAS.flatMap((m) => {
    const p = params[m.chave];
    return p ? [`${m.chave}:${p.peso}:${p.janela}`] : [];
  }).join(',');
  const padrao = METRICAS.map((m) => `${m.chave}:1:${m.janela}`).join(',');
  if (texto === padrao) return '';
  return texto || '-';
}
interface DadosScorecard {
  inicio: string;
  fim: string;
  aviso?: string;
  kpis?: KpiApi[];
  avisos?: string[];
  tabela?: TabelaApi;
  fundos?: string[];
  top5?: string[];
}
interface DadosGraficos {
  fundos: string[];
  abas: { titulo: string; grafico?: GraficoApi; aviso?: string; legenda?: string }[];
}
function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}
export function Scorecard() {
  const [filtros, definir] = useFiltrosUrl({
    cnpjs: '',
    inicio: '',
    fim: '',
    metricas: '',
    fundos: '',
    cdi: true as boolean,
  });
  const cnpjs = filtros.cnpjs ? filtros.cnpjs.split(',') : [];
  const params = lerMetricas(filtros.metricas);
  // Rascunho dos pesos e janelas: a tela responde na hora e a URL só muda quando a
  // digitação para (ESPERA_MS). Se a URL mudar por fora (ex.: Restaurar padrão), o
  // rascunho acompanha (ajuste de estado durante a renderização, sem efeito).
  const [rascunho, setRascunho] = useState(params);
  const [origem, setOrigem] = useState(filtros.metricas);
  if (origem !== filtros.metricas) {
    setOrigem(filtros.metricas);
    setRascunho(params);
  }
  const espera = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(espera.current), []);
  const efetivos = pesosEfetivos(rascunho);
  const base = {
    cnpjs,
    inicio: filtros.inicio || undefined,
    fim: filtros.fim || undefined,
    metricas: filtros.metricas || undefined,
  };
  const resultado = useQuery({
    queryKey: ['fundos.scorecard', base],
    queryFn: ({ signal }) => apiGet<Envelope<DadosScorecard>>(CAMINHO, base, signal),
    enabled: cnpjs.length > 0,
    placeholderData: keepPreviousData,
  });
  const graficos = useQuery({
    queryKey: ['fundos.scorecard.graficos', base, filtros.fundos, filtros.cdi],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosGraficos>>(
        `${CAMINHO}/graficos`,
        { ...base, fundos: filtros.fundos ? filtros.fundos.split('|') : [], cdi: filtros.cdi },
        signal,
      ),
    enabled: cnpjs.length > 0 && !!resultado.data?.data.tabela,
    placeholderData: keepPreviousData,
  });
  const dados = resultado.data?.data;
  const g = graficos.data?.data;
  const pares = useQuery({
    queryKey: ['fundos.peer.pares'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ grupos: { grupo: string; cnpjs: string[] }[] }>>(
        '/fundos/peer-group/pares',
        {},
        signal,
      ),
    staleTime: 60 * 60 * 1000,
  });
  /** Acrescenta os CNPJs de um grupo de pares à lista (sem repetir) e calcula. */
  const importar = (novos: string[]) =>
    definir({ cnpjs: [...new Set([...cnpjs, ...novos])].join(','), fundos: '' });
  const calcular = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const texto = String(new FormData(e.currentTarget).get('cnpjs') ?? '');
    // Como o parse_cnpjs do legado: um por linha, ou separados por vírgula/ponto e vírgula.
    const lista = texto
      .replace(/[,;]/g, '\n')
      .split('\n')
      .map((c) => c.trim())
      .filter(Boolean);
    definir({ cnpjs: [...new Set(lista)].join(','), fundos: '' });
  };
  /** Ligar/desligar grava na hora; peso e janela esperam a digitação parar. */
  const mudar = (chave: string, novo: Param | null, imediato = false) => {
    const proximos = { ...rascunho };
    if (novo) proximos[chave] = novo;
    else delete proximos[chave];
    setRascunho(proximos);
    window.clearTimeout(espera.current);
    const gravar = () => definir({ metricas: escreverMetricas(proximos) });
    if (imediato) gravar();
    else espera.current = window.setTimeout(gravar, ESPERA_MS);
  };
  const restaurar = () => {
    window.clearTimeout(espera.current);
    setRascunho(lerMetricas(''));
    definir({ metricas: '' });
  };
  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Seleção & Scorecard"
        descricao="Score de fundos por retorno, consistência e risco, com pesos e janelas configuráveis, e a performance acumulada dos melhores colocados."
        dadosAte={resultado.data?.meta.dados_ate}
        fonte="ANBIMA Feed (cotas) · Fibery (nomes e pares)"
      />
      <BarraFiltros carregando={resultado.isFetching || graficos.isFetching}>
        <GrupoFiltros titulo="Fundos">
          <form onSubmit={calcular}>
            <Group gap="sm" align="flex-end">
              <Textarea
                key={filtros.cnpjs}
                name="cnpjs"
                label="CNPJs"
                description="Um por linha ou separados por vírgula"
                placeholder={'11.111.111/0001-11\n22.222.222/0001-22'}
                defaultValue={cnpjs.join('\n')}
                autosize
                minRows={3}
                maxRows={6}
                w={230}
              />
              <Stack gap="xs">
                <Menu position="bottom-start" withinPortal>
                  <Menu.Target>
                    <Button variant="light" disabled={!pares.data}>
                      Importar pares
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Acrescenta os CNPJs do grupo</Menu.Label>
                    {(pares.data?.data.grupos ?? []).map((gr) => (
                      <Menu.Item key={gr.grupo} onClick={() => importar(gr.cnpjs)}>
                        {gr.grupo} ({gr.cnpjs.length})
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
                <Button type="submit">Calcular</Button>
              </Stack>
            </Group>
          </form>
        </GrupoFiltros>
        <GrupoFiltros titulo="Período">
          <CampoData
            rotulo="Início"
            valor={filtros.inicio || dados?.inicio || ''}
            aoMudar={(d) => definir({ inicio: d })}
          />
          <CampoData
            rotulo="Fim"
            valor={filtros.fim || dados?.fim || ''}
            aoMudar={(d) => definir({ fim: d })}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Métricas">
          <Stack gap={4}>
            <Table
              withRowBorders={false}
              verticalSpacing={2}
              horizontalSpacing={6}
              fz="xs"
              aria-label="Métricas, pesos e janelas"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Métrica</Table.Th>
                  <Table.Th>Grupo</Table.Th>
                  <Table.Th>Peso</Table.Th>
                  <Table.Th>Janela</Table.Th>
                  <Table.Th>
                    <Tooltip
                      label="Fração do score que a métrica representa com os pesos atuais"
                      withArrow
                    >
                      <span>Peso efetivo</span>
                    </Tooltip>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {METRICAS.map((m, i) => {
                  const p = rascunho[m.chave];
                  return (
                    <Table.Tr key={m.chave}>
                      <Table.Td>
                        <Tooltip label={m.descricao} withArrow position="left">
                          <Checkbox
                            label={m.nome}
                            checked={!!p}
                            onChange={(e) =>
                              mudar(
                                m.chave,
                                e.currentTarget.checked ? { peso: 1, janela: m.janela } : null,
                                true,
                              )
                            }
                            size="xs"
                          />
                        </Tooltip>
                      </Table.Td>
                      {METRICAS[i - 1]?.grupo !== m.grupo && (
                        <Table.Td
                          rowSpan={METRICAS.filter((x) => x.grupo === m.grupo).length}
                          c="dimmed"
                        >
                          {GRUPOS[m.grupo]}
                        </Table.Td>
                      )}
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          aria-label={`Peso ${m.nome}`}
                          disabled={!p}
                          min={0}
                          max={10}
                          step={0.5}
                          decimalSeparator=","
                          value={p?.peso ?? 1}
                          onChange={(v) =>
                            p &&
                            typeof v === 'number' &&
                            v >= 0 &&
                            v <= 10 &&
                            mudar(m.chave, { ...p, peso: v })
                          }
                          w={64}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          aria-label={`Janela ${m.nome}`}
                          disabled={!p}
                          min={1}
                          max={60}
                          suffix="m"
                          value={p?.janela ?? m.janela}
                          onChange={(v) =>
                            p &&
                            typeof v === 'number' &&
                            v >= 1 &&
                            v <= 60 &&
                            mudar(m.chave, { ...p, janela: v })
                          }
                          w={64}
                        />
                      </Table.Td>
                      <Table.Td ta="right" data-testid={`efetivo-${m.chave}`}>
                        {formatarPeso(efetivos[m.chave])}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            <Group justify="flex-end">
              <Button
                variant="subtle"
                size="compact-xs"
                disabled={filtros.metricas === ''}
                onClick={restaurar}
              >
                Restaurar padrão
              </Button>
            </Group>
          </Stack>
        </GrupoFiltros>
        <GrupoFiltros titulo="Performance acumulada">
          <MultiSelect
            label="Fundos"
            searchable
            disabled={!dados?.fundos}
            data={dados?.fundos ?? []}
            value={filtros.fundos ? filtros.fundos.split('|') : (g?.fundos ?? [])}
            onChange={(v) => definir({ fundos: v.join('|') })}
            w={300}
            styles={{ pillsList: { maxHeight: 64, overflowY: 'auto' } }}
          />
          <Checkbox
            label="Incluir CDI"
            checked={filtros.cdi}
            onChange={(e) => definir({ cdi: e.currentTarget.checked })}
            mt={30}
          />
        </GrupoFiltros>
      </BarraFiltros>
      {cnpjs.length === 0 && (
        <Alert color="blue">
          Insira os CNPJs dos fundos (formato XX.XXX.XXX/XXXX-XX) e clique em Calcular.
        </Alert>
      )}
      {resultado.isError && (
        <Alert color="red">{mensagem(resultado.error, 'Não foi possível calcular o score.')}</Alert>
      )}
      {cnpjs.length > 0 && resultado.isPending && <Loader />}
      {dados && (
        <>
          {dados.kpis && (
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              {dados.kpis.map((k) => (
                <Kpi key={k.rotulo} kpi={k} />
              ))}
            </SimpleGrid>
          )}
          <AvisosApi
            avisos={[dados.avisos, resultado.data?.meta.avisos, graficos.data?.meta.avisos]}
          />
          {dados.aviso && <Alert color="yellow">{dados.aviso}</Alert>}
          {dados.tabela && (
            <Secao
              titulo="Scorecard"
              legenda="Score de 0 a 100: percentis das métricas combinados pelos pesos efetivos do painel de métricas (cada grupo pesa a média dos pesos das suas métricas; peso 0 exclui a métrica). Cada janela conta a partir da última cota de cada fundo. Passe o mouse nos títulos das colunas e nas células com — para ver detalhes. Verde mais escuro = melhor quartil."
            >
              <TabelaDados tabela={dados.tabela} alturaAutomatica />
            </Secao>
          )}
          {dados.tabela && (
            <>
              <Secao
                titulo="Performance acumulada"
                legenda="Comparativo a partir do início comum dos fundos escolhidos e, em cada aba, desde a inception do fundo."
              >
                {graficos.isError && (
                  <Alert color="red">
                    {mensagem(graficos.error, 'Erro ao carregar os gráficos.')}
                  </Alert>
                )}
                {g && g.abas.length === 0 && (
                  <Alert color="blue">Selecione fundos para exibir o gráfico de performance.</Alert>
                )}
                {g && g.abas.length > 0 && (
                  <Tabs defaultValue="Comparativo" keepMounted={false} color="dourado">
                    <Tabs.List>
                      {g.abas.map((a) => (
                        <Tabs.Tab key={a.titulo} value={a.titulo}>
                          {a.titulo}
                        </Tabs.Tab>
                      ))}
                    </Tabs.List>
                    {g.abas.map((a) => (
                      <Tabs.Panel key={a.titulo} value={a.titulo} pt="md">
                        {a.legenda && (
                          <Text size="xs" c="dimmed" mb="xs">
                            {a.legenda}
                          </Text>
                        )}
                        {a.grafico ? (
                          <Grafico grafico={a.grafico} />
                        ) : (
                          <Alert color="blue">{a.aviso}</Alert>
                        )}
                      </Tabs.Panel>
                    ))}
                  </Tabs>
                )}
              </Secao>
            </>
          )}
        </>
      )}
    </Pagina>
  );
}
